/** I comandi che fanno della riga un titolo: tre livelli, come il markdown `#`–`###`. */
export type HeadingCommand = 'heading-1' | 'heading-2' | 'heading-3';

export type FormatCommand = 'bold' | 'italic' | HeadingCommand | 'bulleted-list' | 'numbered-list';

export const FORMAT_COMMANDS: readonly FormatCommand[] = [
  'bold',
  'italic',
  'heading-1',
  'heading-2',
  'heading-3',
  'bulleted-list',
  'numbered-list',
];

const HEADING_LEVELS: Readonly<Record<HeadingCommand, 1 | 2 | 3>> = {
  'heading-1': 1,
  'heading-2': 2,
  'heading-3': 3,
};

export function isHeadingCommand(command: FormatCommand): command is HeadingCommand {
  return command in HEADING_LEVELS;
}

/** Il livello del titolo che il comando chiede. */
export function headingLevelOf(command: HeadingCommand): 1 | 2 | 3 {
  return HEADING_LEVELS[command];
}

/**
 * La battuta di tastiera che chiede un comando. Si guarda `code` e non `key` perche' `code` e' il tasto
 * fisico: con Maiusc premuto il tasto «8» produce `key` diversi da tastiera a tastiera — `*` su una
 * disposizione americana, `(` su una italiana — mentre `Digit8` resta `Digit8`. Le lettere avrebbero
 * funzionato anche con `key`; usare la stessa chiave per tutti i comandi evita due regole.
 *
 * I titoli usano Ctrl+Alt con la cifra del livello, come negli editor di documenti: Ctrl+1…3 da soli
 * sono gia' del browser, che ci cambia scheda.
 */
export interface FormatShortcut {
  readonly code: string;
  readonly shift: boolean;
  readonly alt: boolean;
}

const SHORTCUT_BY_COMMAND: Readonly<Record<FormatCommand, FormatShortcut>> = {
  bold: { code: 'KeyB', shift: false, alt: false },
  italic: { code: 'KeyI', shift: false, alt: false },
  'heading-1': { code: 'Digit1', shift: false, alt: true },
  'heading-2': { code: 'Digit2', shift: false, alt: true },
  'heading-3': { code: 'Digit3', shift: false, alt: true },
  'bulleted-list': { code: 'Digit8', shift: true, alt: false },
  'numbered-list': { code: 'Digit7', shift: true, alt: false },
};

/**
 * Le stesse scorciatoie nella forma che le tecnologie assistive leggono (`aria-keyshortcuts`): non e' testo
 * mostrato — quello e' i18n e arriva alla barra come etichetta — ma un valore tecnico con una sintassi sua.
 * `Control` copre anche il Comando del Mac, che la specifica non distingue.
 */
export const ARIA_KEY_SHORTCUTS: Readonly<Record<FormatCommand, string>> = {
  bold: 'Control+B',
  italic: 'Control+I',
  'heading-1': 'Control+Alt+1',
  'heading-2': 'Control+Alt+2',
  'heading-3': 'Control+Alt+3',
  'bulleted-list': 'Control+Shift+8',
  'numbered-list': 'Control+Shift+7',
};

/**
 * Il comando che una battuta chiede, o `undefined` se non ne chiede nessuno. Ctrl e Comando valgono lo
 * stesso: la postazione gira su Windows e su Mac, e la scorciatoia e' la stessa in entrambi.
 */
export function formatCommandFor(event: KeyboardEvent): FormatCommand | undefined {
  if (!event.ctrlKey && !event.metaKey) return undefined;

  return FORMAT_COMMANDS.find((command) => {
    const shortcut = SHORTCUT_BY_COMMAND[command];
    return (
      shortcut.code === event.code &&
      shortcut.shift === event.shiftKey &&
      shortcut.alt === event.altKey
    );
  });
}
